import { Save } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Field } from '../forms/Field';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import { Input } from '../ui/Input';
import {
    Offcanvas,
    OffcanvasBody,
    OffcanvasClose,
    OffcanvasContent,
    OffcanvasDescription,
    OffcanvasFooter,
    OffcanvasHeader,
    OffcanvasTitle,
} from '../ui/Offcanvas';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';

type RowRecord = Record<string, unknown> & { id?: number | string };

export type SngpcFieldOption = { value: string; label: string };

export type SngpcFormField = {
    name: string;
    label: string;
    type?: 'text' | 'email' | 'tel' | 'date' | 'number' | 'select' | 'textarea' | 'checkbox';
    placeholder?: string;
    options?: SngpcFieldOption[];
    required?: boolean;
    fullWidth?: boolean;
    defaultValue?: string | boolean;
};

type Props<TRow extends RowRecord> = {
    open: boolean;
    row: TRow | null;
    entityName: string;
    fields: SngpcFormField[];
    onOpenChange: (open: boolean) => void;
    onSave: (values: Record<string, unknown>) => Promise<void>;
};

function errorMessage(error: unknown): string {
    const response = (error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response;
    const validation = response?.data?.errors;
    return (validation ? Object.values(validation).flat()[0] : null)
        ?? response?.data?.message
        ?? 'Não foi possível salvar o registro.';
}

export function SngpcEntityOffcanvas<TRow extends RowRecord>({
    open,
    row,
    entityName,
    fields,
    onOpenChange,
    onSave,
}: Props<TRow>) {
    const [values, setValues] = useState<Record<string, string | boolean>>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setValues(Object.fromEntries(fields.map((field) => {
            const raw = row?.[field.name] ?? field.defaultValue ?? '';
            if (field.type === 'checkbox') return [field.name, Boolean(raw)];
            if (field.type === 'date' && raw) return [field.name, String(raw).slice(0, 10)];
            return [field.name, raw === null ? '' : String(raw)];
        })));
        setError(null);
    }, [fields, open, row]);

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (saving) return;
        setSaving(true);
        setError(null);
        try {
            const payload = Object.fromEntries(fields.map((field) => {
                const value = values[field.name];
                if (field.type === 'checkbox') return [field.name, Boolean(value)];
                if (field.type === 'number' && value !== '') return [field.name, Number(value)];
                return [field.name, value === '' ? null : value];
            }));
            await onSave(payload);
            onOpenChange(false);
        } catch (requestError) {
            setError(errorMessage(requestError));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Offcanvas open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
            <OffcanvasContent className="nx-sngpc-offcanvas">
                <form className="nexus-offcanvas-form" onSubmit={submit}>
                    <OffcanvasHeader>
                        <OffcanvasTitle>{row ? `Editar ${entityName}` : `Novo ${entityName}`}</OffcanvasTitle>
                        <OffcanvasDescription>
                            {row ? 'Atualize os dados cadastrais.' : 'Preencha os dados para incluir o registro.'}
                        </OffcanvasDescription>
                    </OffcanvasHeader>
                    <OffcanvasBody>
                        {error ? <div className="nx-sngpc-error" role="alert">{error}</div> : null}
                        <div className="nx-form-grid nx-sngpc-offcanvas-grid">
                            {fields.map((field) => (
                                field.type === 'checkbox' ? (
                                    <label key={field.name} className="nx-sngpc-checkbox-field">
                                        <Checkbox
                                            checked={Boolean(values[field.name])}
                                            disabled={saving}
                                            onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.target.checked }))}
                                        />
                                        <span>{field.label}</span>
                                    </label>
                                ) : (
                                    <Field
                                        key={field.name}
                                        label={field.label}
                                        htmlFor={`sngpc-form-${field.name}`}
                                        className={field.fullWidth ? 'nx-sngpc-field--full' : undefined}
                                    >
                                        {field.type === 'select' ? (
                                            <Select
                                                value={String(values[field.name] ?? '')}
                                                required={field.required}
                                                disabled={saving}
                                                onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.target.value }))}
                                            >
                                                <option value="">Selecione</option>
                                                {field.options?.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                            </Select>
                                        ) : field.type === 'textarea' ? (
                                            <Textarea
                                                value={String(values[field.name] ?? '')}
                                                placeholder={field.placeholder}
                                                required={field.required}
                                                disabled={saving}
                                                rows={3}
                                                onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.target.value }))}
                                            />
                                        ) : (
                                            <Input
                                                type={field.type ?? 'text'}
                                                value={String(values[field.name] ?? '')}
                                                placeholder={field.placeholder}
                                                required={field.required}
                                                disabled={saving}
                                                step={field.type === 'number' ? '0.01' : undefined}
                                                onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.target.value }))}
                                            />
                                        )}
                                    </Field>
                                )
                            ))}
                        </div>
                    </OffcanvasBody>
                    <OffcanvasFooter>
                        <OffcanvasClose asChild>
                            <Button type="button" variant="ghost" disabled={saving}>Cancelar</Button>
                        </OffcanvasClose>
                        <Button type="submit" loading={saving} loadingText="Salvando...">
                            <Save size={16} /> Salvar
                        </Button>
                    </OffcanvasFooter>
                </form>
            </OffcanvasContent>
        </Offcanvas>
    );
}
